import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	BarChart3,
	Building2,
	CircleEllipsis,
	Inbox,
	LayoutDashboard,
	Layers,
	Package,
	Search,
	Settings,
	ShieldCheck,
	ShieldX,
	ShoppingCart,
	Truck,
	UserCog,
	Wallet,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { getPermissionDisplayLabel, permissionActionBadgeClass } from "@/lib/permission-label";
import { buildPermissionUiGroups, getPermissionActionKey, type PermissionGroupId } from "@/lib/permission-groups";

const GROUP_ICONS: Record<PermissionGroupId, React.ComponentType<{ className?: string }>> = {
	overview: LayoutDashboard,
	sales_pos: ShoppingCart,
	catalog: Package,
	procurement: Truck,
	finance: Wallet,
	reports: BarChart3,
	organization: Building2,
	administration: UserCog,
	system: Settings,
	other: CircleEllipsis,
};

export type GroupedPermissionRecord = {
	id: string | number;
	name: string;
	type?: string;
	action?: string;
};

export type GroupedPermissionsPanelProps<T extends GroupedPermissionRecord> = {
	permissions: T[];
	className?: string;
	scrollAreaClassName?: string;
	showSearch?: boolean;
	selection?: {
		selectedIds: string[];
		onToggle: (permissionId: string, checked: boolean) => void;
		disabled?: boolean;
	};
	grantOverlay?: {
		grantedIds: Set<string>;
	};
	readonlyGranted?: boolean;
	idPrefix?: string;
	renderExtras?: (permission: T) => React.ReactNode;
};

function normalizeId(id: string | number): string {
	return String(id);
}

export function GroupedPermissionsPanel<T extends GroupedPermissionRecord>(props: GroupedPermissionsPanelProps<T>) {
	const {
		permissions,
		className,
		scrollAreaClassName,
		showSearch = true,
		selection,
		grantOverlay,
		readonlyGranted,
		idPrefix = "perm",
		renderExtras,
	} = props;
	const { t } = useTranslation();
	const [query, setQuery] = useState("");
	const [selectedOnly, setSelectedOnly] = useState(false);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		return permissions.filter(p => {
			if (selectedOnly && selection && !selection.selectedIds.includes(String(p.id))) return false;
			const label = getPermissionDisplayLabel(t, p);
			const hay = `${p.name} ${label}`.toLowerCase();
			return hay.includes(q);
		});
	}, [permissions, query, t, selectedOnly, selection]);

	const groups = useMemo(() => buildPermissionUiGroups(filtered), [filtered]);

	const totalCount = permissions.length;
	const selectedCount = selection?.selectedIds.length ?? 0;

	return (
		<div className={cn("flex flex-col gap-4", className)}>
			{selection && (
				<div className="rounded-lg bg-muted p-3 text-sm space-y-2">
					<p>{t("workspace.accessHelp")}</p>
					<p className="text-muted-foreground">{t("workspace.permissionsHint")}</p>
					<label className="flex min-h-11 items-center gap-2 cursor-pointer">
						<Checkbox checked={selectedOnly} onCheckedChange={v => setSelectedOnly(v === true)} />
						{t("workspace.selectedOnly")}
					</label>
				</div>
			)}
			{(showSearch || selection) && (
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
					{showSearch && (
						<div className="relative w-full max-w-md">
							<Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
							<Input
								value={query}
								onChange={e => setQuery(e.target.value)}
								placeholder={t("permissions.ui.searchPlaceholder")}
								className={cn(
									"h-11 rounded-xl border-border/60 bg-muted/30 ps-10 shadow-sm",
									"placeholder:text-muted-foreground/70 focus-visible:ring-primary/25",
								)}
								aria-label={t("permissions.ui.searchPlaceholder")}
							/>
						</div>
					)}
					{selection && (
						<div
							className={cn(
								"flex items-center justify-between gap-3 rounded-xl border border-primary/15 bg-primary/[0.06] px-4 py-2.5 shadow-sm",
								!showSearch && "w-full sm:max-w-md sm:ms-auto",
							)}
						>
							<span className="text-sm font-medium text-foreground">
								{t("permissions.ui.selectionSummary")}
							</span>
							<Badge variant="secondary" className="tabular-nums font-semibold">
								{t("permissions.ui.selectedCount", { selected: selectedCount, total: totalCount })}
							</Badge>
						</div>
					)}
				</div>
			)}

			<div
				className={cn(
					"rounded-2xl border border-border/70 bg-card",
					scrollAreaClassName ?? "max-h-[min(28rem,70vh)] overflow-y-auto overscroll-contain",
				)}
			>
				{groups.length === 0 ? (
					<div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
						<div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/80 text-muted-foreground shadow-inner">
							<Inbox className="h-7 w-7 opacity-80" />
						</div>
						<div className="space-y-1">
							<p className="text-sm font-medium text-foreground">{t("permissions.ui.emptyTitle")}</p>
							<p className="max-w-sm text-sm text-muted-foreground">
								{t("permissions.ui.emptyDescription")}
							</p>
						</div>
					</div>
				) : (
					<Accordion
						key={`${query}-${selectedOnly}-${groups.length}`}
						type="multiple"
						defaultValue={query || selectedOnly ? groups.map(g => g.groupId) : []}
						className="divide-y divide-border/60 px-1 py-1"
					>
						{groups.map(group => {
							const Icon = GROUP_ICONS[group.groupId];
							const groupCount = group.modules.reduce((n, m) => n + m.items.length, 0);
							return (
								<AccordionItem key={group.groupId} value={group.groupId} className="border-0 px-2 py-1">
									<AccordionTrigger className="rounded-xl px-3 py-3.5 hover:no-underline hover:bg-muted/40 [&[data-state=open]]:bg-muted/25">
										<div className="flex flex-1 items-start gap-3 text-start min-w-0">
											<span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm">
												<Icon className="h-5 w-5" aria-hidden />
											</span>
											<div className="min-w-0 flex-1 space-y-0.5">
												<div className="flex flex-wrap items-center gap-2">
													<span className="font-semibold leading-snug text-foreground">
														{t(`permissions.groups.${group.groupId}.title`)}
													</span>
													<Badge
														variant="outline"
														className="h-5 border-border/80 px-2 text-[11px] font-medium tabular-nums text-muted-foreground"
													>
														{selection
															? `${group.modules.flatMap(m => m.items).filter(p => selection.selectedIds.includes(String(p.id))).length} / ${groupCount}`
															: groupCount}
													</Badge>
												</div>
												<p className="text-xs leading-relaxed text-muted-foreground line-clamp-2">
													{t(`permissions.groups.${group.groupId}.description`)}
												</p>
											</div>
										</div>
									</AccordionTrigger>
									<AccordionContent className="pb-4 pt-1">
										<div className="grid gap-4 lg:grid-cols-2">
											{group.modules.map(({ moduleKey, items }) => (
												<div
													key={moduleKey}
													className="overflow-hidden rounded-xl border border-border/60 bg-background/90 shadow-sm ring-1 ring-black/[0.03] dark:ring-white/[0.04]"
												>
													<div className="flex items-center gap-2 border-b border-border/60 bg-muted/25 px-3 py-4">
														<Layers className="h-4 w-4 shrink-0 text-primary" aria-hidden />
														<span className="text-sm font-semibold tracking-tight text-foreground">
															{t(`permissions.types.${moduleKey}`, moduleKey)}
														</span>
														<Badge
															variant="secondary"
															className="ms-auto h-5 min-w-[1.5rem] justify-center px-1.5 text-[10px] tabular-nums"
														>
															{items.length}
														</Badge>
													</div>
													<ul className="divide-y divide-border/50 p-1.5">
														{items.map(p => {
															const idStr = normalizeId(p.id);
															const inputId = `${idPrefix}-${idStr}`;
															const action = getPermissionActionKey(p);
															const checked =
																selection?.selectedIds.includes(idStr) ?? false;
															const granted = grantOverlay
																? grantOverlay.grantedIds.has(idStr)
																: readonlyGranted
																	? true
																	: undefined;
															const label = getPermissionDisplayLabel(t, p);

															return (
																<li key={idStr}>
																	<div
																		className={cn(
																			"group flex min-h-11 flex-col gap-2 rounded-lg px-2 py-2 transition-colors sm:flex-row sm:items-center sm:justify-between sm:gap-3",
																			"hover:bg-muted/45",
																		)}
																	>
																		<div className="flex min-w-0 flex-1 items-start gap-3">
																			{selection && !selection.disabled ? (
																				<Checkbox
																					id={inputId}
																					checked={checked}
																					className="mt-1"
																					onCheckedChange={v =>
																						selection.onToggle(
																							idStr,
																							v === true,
																						)
																					}
																				/>
																			) : grantOverlay ? (
																				<span
																					className={cn(
																						"mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
																						granted
																							? "bg-emerald-500/12 text-success dark:text-emerald-400"
																							: "bg-muted text-muted-foreground",
																					)}
																					aria-hidden
																				>
																					{granted ? (
																						<ShieldCheck className="h-3.5 w-3.5" />
																					) : (
																						<ShieldX className="h-3.5 w-3.5 opacity-70" />
																					)}
																				</span>
																			) : readonlyGranted ? (
																				<span
																					className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/12 text-success dark:text-emerald-400"
																					aria-hidden
																				>
																					<ShieldCheck className="h-3.5 w-3.5" />
																				</span>
																			) : null}

																			<div className="min-w-0 flex-1 space-y-1">
																				{selection && !selection.disabled ? (
																					<Label
																						htmlFor={inputId}
																						className="cursor-pointer text-sm font-medium leading-snug text-foreground"
																					>
																						{label}
																					</Label>
																				) : (
																					<span className="text-sm font-medium leading-snug text-foreground">
																						{label}
																					</span>
																				)}
																				{action ? (
																					<div className="flex flex-wrap gap-1.5">
																						<Badge
																							variant="outline"
																							className={cn(
																								"h-5 px-2 text-[10px] font-semibold tracking-wide",
																								permissionActionBadgeClass(
																									action,
																								),
																							)}
																						>
																							{t(
																								`permissions.actionLabels.${action}`,
																								action,
																							)}
																						</Badge>
																					</div>
																				) : null}
																			</div>
																		</div>
																		{renderExtras ? (
																			<div className="shrink-0 ps-9 text-xs text-muted-foreground sm:ps-0 sm:text-end">
																				{renderExtras(p)}
																			</div>
																		) : null}
																	</div>
																</li>
															);
														})}
													</ul>
												</div>
											))}
										</div>
									</AccordionContent>
								</AccordionItem>
							);
						})}
					</Accordion>
				)}
			</div>
		</div>
	);
}
